<template>
    <div class="space-y-6">
        <h2 class="text-heading-md">Combobox Examples</h2>
        <div class="grid grid-cols-2 gap-4">
            <div>
                <div class="space-y-4">
                    <DsCombobox
                        v-model="selectedValue"
                        v-model:open="isOpen"
                        v-model:searchTerm="searchTerm"
                    >
                        <DsComboboxAnchor
                            asChild
                            class="w-full"
                        >
                            <DsTextInput
                                v-model="searchTerm"
                                label="Fruits"
                                placeholder="Search fruits..."
                                type="text"
                                @focus="handleOpen"
                                @input="handleOpen"
                            />
                        </DsComboboxAnchor>
                        <DsComboboxContent class="max-h-[200px] overflow-y-auto p-2">
                            <DsComboboxItem
                                v-for="option in filteredOptions"
                                :key="option.value"
                                class="rounded-lg p-2"
                                :value="option.value"
                                @select="handleItemSelected(option)"
                            >
                                <span>{{ option.label }}</span>
                            </DsComboboxItem>
                            <DsComboboxEmpty v-if="filteredOptions.length === 0">No fruits found.</DsComboboxEmpty>
                        </DsComboboxContent>
                    </DsCombobox>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import DsCombobox from '@/components/design-system/form/combobox/DsCombobox.vue';
import DsComboboxAnchor from '@/components/design-system/form/combobox/DsComboboxAnchor.vue';
import DsComboboxContent from '@/components/design-system/form/combobox/DsComboboxContent.vue';
import DsComboboxEmpty from '@/components/design-system/form/combobox/DsComboboxEmpty.vue';
import DsComboboxItem from '@/components/design-system/form/combobox/DsComboboxItem.vue';
import DsTextInput from '@ds/form/input/text/DsTextInput.vue';
import { computed, ref } from 'vue';

const selectedValue = ref('');
const searchTerm = ref('');
const isOpen = ref(false);

const options = ref([
    {
        value: 'apple',
        label: 'Apple',
    },
    {
        value: 'banana',
        label: 'Banana',
    },
    {
        value: 'cherry',
        label: 'Cherry',
    },
    {
        value: 'date',
        label: 'Date',
    },
    {
        value: 'elderberry',
        label: 'Elderberry',
    },
    {
        value: 'fig',
        label: 'Fig',
    },
]);

const filteredOptions = computed(() => {
    if (!searchTerm.value) {
        return options.value;
    }
    return options.value.filter(option => option.label.toLowerCase().includes(searchTerm.value.toLowerCase()));
});

const handleOpen = () => {
    if (searchTerm.value.trim()) {
        isOpen.value = true;
    }
};

const handleItemSelected = (option: { value: string; label: string }) => {
    selectedValue.value = option.value;
    searchTerm.value = option.label;
    isOpen.value = false;
};
</script>

<style scoped lang="scss">
.ds-combobox-item {
    &:hover {
        @apply bg-semantic-secondary-hover outline-none;
    }
}

.ds-combobox-item:active:not(:focus-visible) {
    @apply bg-semantic-secondary-pressed outline-none;
}
</style>
